import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getPantallaCampana } from "../api/campana";
import { anunciar, desbloquearAudio, fraseCampana } from "../utils/campanaSonido";
import logo from "../assets/logo.png";
import "./CampanaScreen.css";

const POLL_MS = 4000;
const MAX_LISTA = 12;

/** La pantalla habla el idioma del negocio, no el del navegador de la barra. */
const UI = {
  es: {
    activar: "Activar campana",
    activarAyuda: "Toca para que esta pantalla pueda sonar. Déjala abierta y con volumen.",
    activa: "Campana activa",
    desactivada: "La campana está desactivada para este negocio. Actívala desde la wallet.",
    invalido: "Este enlace de campana no es válido o se ha cambiado por uno nuevo.",
    sinConexion: "Sin conexión. Reintentando…",
    probar: "Probar",
    vacio: "Aún no ha sonado ninguna propina.",
    prueba: "Prueba",
    propina: "Propina para el equipo",
  },
  ca: {
    activar: "Activar la campana",
    activarAyuda: "Toca perquè aquesta pantalla pugui sonar. Deixa-la oberta i amb volum.",
    activa: "Campana activa",
    desactivada: "La campana està desactivada per a aquest negoci. Activa-la des de la wallet.",
    invalido: "Aquest enllaç de campana no és vàlid o s'ha canviat per un de nou.",
    sinConexion: "Sense connexió. Tornant-ho a provar…",
    probar: "Provar",
    vacio: "Encara no ha sonat cap propina.",
    prueba: "Prova",
    propina: "Propina per a l'equip",
  },
  en: {
    activar: "Turn on the bell",
    activarAyuda: "Tap so this screen can ring. Keep it open with the volume up.",
    activa: "Bell on",
    desactivada: "The bell is turned off for this business. Turn it on from the wallet.",
    invalido: "This bell link isn't valid or has been replaced by a new one.",
    sinConexion: "Offline. Retrying…",
    probar: "Test",
    vacio: "No tips have rung yet.",
    prueba: "Test",
    propina: "A tip for the team",
  },
};

const hora = (fecha) =>
  fecha ? new Date(fecha).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

const CampanaScreen = () => {
  const { token } = useParams();
  const [estado, setEstado] = useState("cargando"); // cargando | invalido | listo
  const [datos, setDatos] = useState(null);
  const [lista, setLista] = useState([]);
  const [activada, setActivada] = useState(false);
  const [sinConexion, setSinConexion] = useState(false);
  const [sonando, setSonando] = useState(null);

  const cursorRef = useRef(null);
  const colaRef = useRef([]);
  const reproduciendoRef = useRef(false);
  const activadaRef = useRef(false);
  const idiomaRef = useRef("es");
  const wakeLockRef = useRef(null);

  const idioma = datos?.idioma || "es";
  const ui = UI[idioma] || UI.es;
  idiomaRef.current = idioma;

  const procesarCola = useCallback(async () => {
    if (reproduciendoRef.current || !activadaRef.current) return;
    reproduciendoRef.current = true;
    while (colaRef.current.length) {
      const evento = colaRef.current.shift();
      setSonando(evento.id);
      try {
        await anunciar(evento, idiomaRef.current);
      } catch (error) {
        console.warn("No se pudo reproducir la campana:", error);
      }
      setSonando(null);
    }
    reproduciendoRef.current = false;
  }, []);

  const consultar = useCallback(async () => {
    try {
      const res = await getPantallaCampana(token, cursorRef.current);
      const d = res.data;
      setSinConexion(false);
      setDatos(d);
      setEstado("listo");
      if (!cursorRef.current) {
        setLista(d.historial || []);
      } else if (d.nuevos?.length) {
        setLista((prev) => [...d.nuevos.slice().reverse(), ...prev].slice(0, MAX_LISTA));
        if (activadaRef.current) {
          colaRef.current.push(...d.nuevos);
          procesarCola();
        }
      }
      cursorRef.current = d.cursor;
    } catch (error) {
      if (error?.response?.status === 404) setEstado("invalido");
      else setSinConexion(true);
    }
  }, [token, procesarCola]);

  const estadoRef = useRef(estado);
  estadoRef.current = estado;

  useEffect(() => {
    consultar();
    const id = setInterval(() => {
      if (estadoRef.current !== "invalido") consultar();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [consultar]);

  // Pantalla siempre encendida mientras la campana está activa
  useEffect(() => {
    if (!activada || !("wakeLock" in navigator)) return undefined;
    const pedir = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch (error) {
        // Sin permiso o sin batería: la campana sigue funcionando
      }
    };
    const alVolver = () => {
      if (document.visibilityState === "visible") pedir();
    };
    pedir();
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      document.removeEventListener("visibilitychange", alVolver);
      wakeLockRef.current?.release?.().catch(() => {});
    };
  }, [activada]);

  const activar = () => {
    desbloquearAudio();
    activadaRef.current = true;
    setActivada(true);
  };

  const probar = () => {
    colaRef.current.push({ id: `prueba-${Date.now()}`, frase: "gracias_equipo", prueba: true });
    procesarCola();
  };

  if (estado === "cargando") {
    return <div className="campana-screen campana-screen--centro">…</div>;
  }

  if (estado === "invalido") {
    return (
      <div className="campana-screen campana-screen--centro">
        <img src={logo} alt="Tipify" className="campana-screen__tipify" />
        <p className="campana-screen__aviso">{UI.es.invalido}</p>
        <p className="campana-screen__aviso">{UI.ca.invalido}</p>
      </div>
    );
  }

  const negocio = datos?.negocio || {};

  return (
    <div className="campana-screen">
      <header className="campana-screen__header">
        {negocio.logoURL ? (
          <img
            src={negocio.logoURL}
            alt=""
            className="campana-screen__logo"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        <h1 className="campana-screen__negocio">{negocio.nombre_comercial}</h1>
        {activada && datos?.activa ? (
          <span className="campana-screen__estado">
            <span className="campana-screen__punto" /> {ui.activa}
          </span>
        ) : null}
      </header>

      {sinConexion ? <p className="campana-screen__offline">{ui.sinConexion}</p> : null}

      {!datos?.activa ? (
        <p className="campana-screen__aviso">{ui.desactivada}</p>
      ) : !activada ? (
        <div className="campana-screen__activar-wrap">
          <button type="button" className="campana-screen__activar" onClick={activar}>
            <span aria-hidden="true">🔔</span> {ui.activar}
          </button>
          <p className="campana-screen__ayuda">{ui.activarAyuda}</p>
        </div>
      ) : (
        <div className={`campana-screen__campana${sonando ? " is-ringing" : ""}`} aria-hidden="true">
          🔔
        </div>
      )}

      <ul className="campana-screen__lista">
        {lista.length === 0 ? <li className="campana-screen__vacio">{ui.vacio}</li> : null}
        {lista.map((e) => (
          <li key={e.id} className={`campana-screen__item${sonando === e.id ? " is-ringing" : ""}`}>
            <span className="campana-screen__icono" aria-hidden="true">🔔</span>
            <span className="campana-screen__mensaje">
              {fraseCampana(e.frase, idioma) || ui.propina}
              {e.prueba ? <em className="campana-screen__prueba"> · {ui.prueba}</em> : null}
            </span>
            <span className="campana-screen__hora">{hora(e.createdAt)}</span>
          </li>
        ))}
      </ul>

      {activada && datos?.activa ? (
        <button type="button" className="campana-screen__probar" onClick={probar}>
          {ui.probar}
        </button>
      ) : null}

      <footer className="campana-screen__footer">
        <img src={logo} alt="Tipify" className="campana-screen__tipify" />
      </footer>
    </div>
  );
};

export default CampanaScreen;
